import {
  Card, Stack, Table, Badge, Loader, Center, Tabs, Button, Group, Text,
  Switch, MultiSelect, Title, Divider, Alert,
} from "@mantine/core";
import { notifications as mantineNotifications } from "@mantine/notifications";
import { IconInfoCircle } from "@tabler/icons-react";
import { useEffect } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { apiClient } from "../../../shared/api/client";
import { useResource } from "../../../shared/api";
import { PageHeader, EmptyState } from "../../../shared/ui";
import { useWebSocket } from "../../../shared/ws";

const SEVERITY_COLOR: Record<string, string> = {
  INFO: "blue", WARNING: "yellow", CRITICAL: "red",
};

export function NotificationsPage() {
  const qc = useQueryClient();

  // WS canlı push: yeni bildirim geldiğinde listeyi tazele.
  // WS bağlıyken polling kapalı, koparsa 60 sn fallback.
  const { lastMessage, status: wsStatus } = useWebSocket<any>("/ws/notifications/");
  const wsOk = wsStatus === "OPEN";

  // Bana ait + role/tenant broadcast'leri (recipient filter backend tarafında)
  const { data: notifData, isLoading } = useQuery<any>({
    queryKey: ["notifications", "list", "mine"],
    queryFn: async () => {
      const { data } = await apiClient.get("/api/v1/notifications/", {
        params: { mine: 1, ordering: "-created_at" },
      });
      return data;
    },
    refetchInterval: wsOk ? false : 60000,
  });

  const { data: rulesData } = useResource<any>("notification-rules");

  useEffect(() => {
    if (lastMessage) {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    }
  }, [lastMessage, qc]);

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.post(`/api/v1/notifications/${id}/mark-read/`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post(
        "/api/v1/notifications/mark-all-read/",
        { mine: 1 },
        { params: { mine: 1 } },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const notifs: any[] = notifData?.results || notifData || [];
  const rules: any[] = rulesData?.results || rulesData || [];
  const unreadCount = notifs.filter((n) => !n.read_at).length;

  if (isLoading) return <Center h={300}><Loader /></Center>;

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <PageHeader
          title="Bildirimler"
          subtitle={`${notifs.length} bildirim · ${unreadCount} okunmamış · ${rules.length} kural`}
        />
        {unreadCount > 0 && (
          <Button
            variant="light"
            onClick={() => markAllRead.mutate()}
            loading={markAllRead.isPending}
          >
            Tümünü okundu işaretle ({unreadCount})
          </Button>
        )}
      </Group>

      <Tabs defaultValue="inbox">
        <Tabs.List>
          <Tabs.Tab value="inbox">
            Gelen ({notifs.length})
            {unreadCount > 0 && (
              <Badge ml="xs" size="xs" color="red">{unreadCount}</Badge>
            )}
          </Tabs.Tab>
          <Tabs.Tab value="rules">Kurallar ({rules.length})</Tabs.Tab>
          <Tabs.Tab value="preferences">Tercihler</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="inbox" pt="md">
          {notifs.length === 0 ? (
            <EmptyState title="Bildirim yok" />
          ) : (
            <Card padding="sm" radius="md" withBorder>
              <Table.ScrollContainer minWidth={680}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Tarih</Table.Th>
                    <Table.Th>Önem</Table.Th>
                    <Table.Th>Başlık</Table.Th>
                    <Table.Th>Mesaj</Table.Th>
                    <Table.Th>Durum</Table.Th>
                    <Table.Th>İşlem</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {notifs.slice(0, 200).map((n: any) => (
                    <Table.Tr key={n.id} style={{ opacity: n.read_at ? 0.55 : 1 }}>
                      <Table.Td>
                        <Text size="xs">
                          {new Date(n.created_at).toLocaleString("tr-TR")}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={SEVERITY_COLOR[n.severity] || "gray"} size="sm">
                          {n.severity}
                        </Badge>
                      </Table.Td>
                      <Table.Td fw={n.read_at ? 400 : 600}>{n.title}</Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed" lineClamp={2}>
                          {n.body || n.message || ""}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {n.read_at ? (
                          <Badge color="gray" size="sm">Okundu</Badge>
                        ) : (
                          <Badge color="blue" size="sm">Yeni</Badge>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {!n.read_at && (
                          <Button
                            size="xs"
                            variant="light"
                            onClick={() => markRead.mutate(n.id)}
                            loading={markRead.isPending}
                          >
                            İşaretle
                          </Button>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              </Table.ScrollContainer>
            </Card>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="rules" pt="md">
          {rules.length === 0 ? (
            <EmptyState title="Kural yok" />
          ) : (
            <Card padding="sm" radius="md" withBorder>
              <Table.ScrollContainer minWidth={560}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Olay</Table.Th>
                    <Table.Th>Kanal</Table.Th>
                    <Table.Th>Hedef Rol</Table.Th>
                    <Table.Th>Önem</Table.Th>
                    <Table.Th>Aktif</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rules.map((r: any) => (
                    <Table.Tr key={r.id}>
                      <Table.Td><Badge variant="light">{r.event_name}</Badge></Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          {(r.channels || []).map((c: string) => (
                            <Badge key={c} size="xs">{c}</Badge>
                          ))}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          {(r.target_roles || []).map((t: string) => (
                            <Badge key={t} size="xs" color="gray">{t}</Badge>
                          ))}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={SEVERITY_COLOR[r.severity] || "gray"} size="sm">
                          {r.severity}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{r.is_enabled ? "✓" : "—"}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              </Table.ScrollContainer>
            </Card>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="preferences" pt="md">
          <PreferencesPanel />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Tenant bildirim tercihleri — kritik stok için kanal seçimi
// ---------------------------------------------------------------------------
const ROLE_OPTIONS = [
  { value: "OWNER", label: "OWNER" },
  { value: "MANAGER", label: "MANAGER" },
  { value: "CHEF", label: "CHEF" },
  { value: "WAITER", label: "WAITER" },
  { value: "CASHIER", label: "CASHIER" },
  { value: "ACCOUNTANT", label: "ACCOUNTANT" },
];

function useSettingValue<T>(key: string) {
  return useQuery({
    queryKey: ["setting", key],
    queryFn: async () => {
      const { data } = await apiClient.get(`/api/v1/settings/${key}/value/`);
      return data as { key: string; value: T };
    },
  });
}

function useSettingMutation(key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (value: any) => {
      const { data } = await apiClient.put(`/api/v1/settings/${key}/value/`, { value });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["setting", key] });
      mantineNotifications.show({ message: "Tercih güncellendi", color: "green" });
    },
    onError: (err: any) => {
      mantineNotifications.show({
        message: err?.response?.data?.detail || "Güncelleme başarısız",
        color: "red",
      });
    },
  });
}

function PreferencesPanel() {
  const inApp = useSettingValue<boolean>("stock_alert_notify_in_app");
  const viaEmail = useSettingValue<boolean>("stock_alert_notify_email");
  const targetRoles = useSettingValue<string[]>("stock_alert_target_roles");

  const updateInApp = useSettingMutation("stock_alert_notify_in_app");
  const updateEmail = useSettingMutation("stock_alert_notify_email");
  const updateRoles = useSettingMutation("stock_alert_target_roles");

  return (
    <Card padding="md" radius="md" withBorder>
      <Title order={5}>Kritik Stok Bildirimleri</Title>
      <Text size="sm" c="dimmed" mt={4}>
        Eşik altı ve stok bitti olaylarında hangi kanallardan ve kimlere
        bildirim gönderileceğini seçin. Değişiklikler sonraki uyarıda devreye girer.
      </Text>
      <Divider my="md" />

      <Stack gap="md">
        <Switch
          label="Uygulama içi bildirim (zil ikonu + Bildirimler sayfası)"
          description="Anlık WS push + kalıcı kayıt"
          checked={!!inApp.data?.value}
          onChange={(e) => updateInApp.mutate(e.currentTarget.checked)}
          disabled={inApp.isLoading || updateInApp.isPending}
        />
        <Switch
          label="E-posta gönder"
          description="Hedef rollerdeki kullanıcılara Resend üzerinden mail at"
          checked={!!viaEmail.data?.value}
          onChange={(e) => updateEmail.mutate(e.currentTarget.checked)}
          disabled={viaEmail.isLoading || updateEmail.isPending}
        />
        <MultiSelect
          label="Hedef roller"
          description="Default: OWNER + MANAGER + CHEF (yedekli — birinden kaçarsa diğeri görür). Boş bırakılırsa tenant geneline broadcast."
          data={ROLE_OPTIONS}
          value={targetRoles.data?.value || []}
          onChange={(v) => updateRoles.mutate(v)}
          disabled={targetRoles.isLoading || updateRoles.isPending}
        />

        {!inApp.data?.value && !viaEmail.data?.value && (
          <Alert color="yellow" variant="light" icon={<IconInfoCircle size={16} />}>
            Hiçbir kanal seçili değil — kritik stok olayları için bildirim gönderilmez.
            Stok hâlâ <code>StockAlert</code> tablosuna kaydedilir.
          </Alert>
        )}
      </Stack>
    </Card>
  );
}
